import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_ORIGIN } from '../config/env';
import { useParams } from 'react-router-dom';

// Phone-side receipt capture page (Phase 3 Slice F4), reached only via a QR
// scan of /scan/:token. Renders OUTSIDE the app shell: no auth, no Layout —
// the single-use handoff token from the URL is the only credential, and all
// API calls here use raw fetch (never services/api.js: its auth header and
// 401→login interceptor must stay away from this page).
//
// Dev (CRA dev server): relative /api rides the package.json proxy to the
// local backend from ANY origin — localhost, a LAN IP during phone testing,
// or an https tunnel. Production builds talk to Railway directly.
const API_BASE = `${API_ORIGIN}/api`; // config/env.js (P1-B): '' in dev → '/api' rides the proxy

const COLORS = {
  bg: '#0D0D0D',
  card: '#1E1E1E',
  gold: '#D4A94E',
  text: '#FFFFFF',
  muted: '#A0A0A0',
  danger: '#EF4444',
  green: '#22C55E'
};

const styles = {
  // height (not minHeight) + hidden overflow: in portrait, an unconstrained
  // camera preview grows taller than the visible viewport and pushes the
  // action bar under Safari's bottom toolbar. dvh tracks the toolbar's
  // dynamic size; the media elements below are capped so the bar always
  // fits inside the visible area.
  page: {
    height: '100dvh',
    overflow: 'hidden',
    background: COLORS.bg,
    color: COLORS.text,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px',
    paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    textAlign: 'center'
  },
  title: { fontSize: '18px', fontWeight: 700, marginBottom: '8px' },
  sub: { fontSize: '14px', color: COLORS.muted, marginBottom: '20px' },
  button: {
    background: COLORS.gold,
    color: '#111111',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  secondaryButton: {
    background: 'transparent',
    color: COLORS.muted,
    border: `1px solid ${COLORS.muted}`,
    borderRadius: '10px',
    padding: '14px 28px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  videoWrap: {
    position: 'relative',
    width: '100%',
    maxWidth: '480px',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#000'
  },
  video: { width: '100%', maxHeight: '60dvh', objectFit: 'cover', display: 'block' },
  bracket: (pos) => ({
    position: 'absolute',
    width: '28px',
    height: '28px',
    borderColor: COLORS.gold,
    borderStyle: 'solid',
    borderWidth: 0,
    ...(pos.includes('top') ? { top: '10%', borderTopWidth: '3px' } : { bottom: '12%', borderBottomWidth: '3px' }),
    ...(pos.includes('left') ? { left: '6%', borderLeftWidth: '3px' } : { right: '6%', borderRightWidth: '3px' })
  }),
  bar: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
    width: '100%',
    maxWidth: '480px',
    justifyContent: 'center',
    flexShrink: 0,
    paddingBottom: 'env(safe-area-inset-bottom)'
  },
  capturedImg: {
    width: '100%',
    maxWidth: '480px',
    maxHeight: '60dvh',
    objectFit: 'contain',
    borderRadius: '12px',
    display: 'block'
  },
  bigIcon: { fontSize: '48px', marginBottom: '12px' }
};

const ScanReceipt = () => {
  const { token } = useParams(); // design: ONE route, token from params only
  const [step, setStep] = useState('validating');
  // validating | ready | camera | captured | uploading | done
  // terminal: invalid-token | expired | no-camera | upload-failed
  const [reportTitle, setReportTitle] = useState('');
  const [captured, setCaptured] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // iOS Safari: the <video> isn't in the DOM when getUserMedia resolves —
  // park the stream and attach it once the camera step has rendered
  // (deferred-attach pattern from the eliteverify reference).
  const pendingStreamRef = useRef(null);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await fetch(`${API_BASE}/expenses/handoff/${token}/session`);
        if (res.status === 404) return setStep('invalid-token');
        if (res.status === 410) return setStep('expired');
        if (!res.ok) return setStep('invalid-token');
        const data = await res.json();
        setReportTitle(data.report_title || '');
        setStep('ready');
      } catch (e) {
        setStep('invalid-token');
      }
    };
    validate();
  }, [token]);

  useEffect(() => {
    if (step === 'camera' && pendingStreamRef.current && videoRef.current) {
      videoRef.current.srcObject = pendingStreamRef.current;
      pendingStreamRef.current = null;
    }
  }, [step]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => stopStream, [stopStream]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      } else {
        pendingStreamRef.current = stream;
      }
      setCaptured(null);
      setStep('camera');
    } catch (e) {
      setStep('no-camera');
    }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    // Receipt-friendly tall frame: 90% width x 80% height, centered.
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const cw = Math.round(vw * 0.9);
    const ch = Math.round(vh * 0.8);
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    canvas.getContext('2d').drawImage(video, (vw - cw) / 2, (vh - ch) / 2, cw, ch, 0, 0, cw, ch);
    setCaptured(canvas.toDataURL('image/jpeg', 0.85));
    setStep('captured');
  };

  const upload = async () => {
    setStep('uploading');
    setUploadError(null);
    try {
      const blob = await (await fetch(captured)).blob();
      const pre = await fetch(`${API_BASE}/expenses/handoff/${token}/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_type: 'image/jpeg', file_size: blob.size })
      });
      if (pre.status === 410) { stopStream(); return setStep('expired'); }
      if (pre.status === 409) {
        setUploadError('This report was submitted — it can no longer receive receipts.');
        return setStep('upload-failed');
      }
      if (!pre.ok) throw new Error('presign-failed');
      const { upload_url, s3_key } = await pre.json();

      const put = await fetch(upload_url, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'image/jpeg' }
      });
      if (!put.ok) throw new Error('put-failed');

      const conf = await fetch(`${API_BASE}/expenses/handoff/${token}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3_key, original_filename: `phone-receipt-${Date.now()}.jpg` })
      });
      if (conf.status === 410) { stopStream(); return setStep('expired'); }
      if (conf.status === 409) {
        setUploadError('This report was submitted — it can no longer receive receipts.');
        return setStep('upload-failed');
      }
      if (!conf.ok) throw new Error('confirm-failed');

      // Success is declared ONLY on the server's confirm 2xx.
      stopStream();
      setStep('done');
    } catch (e) {
      setStep('upload-failed');
    }
  };

  const terminal = (icon, title, sub, extra = null) => (
    <div style={styles.page}>
      <div style={styles.bigIcon}>{icon}</div>
      <div style={styles.title}>{title}</div>
      <div style={styles.sub}>{sub}</div>
      {extra}
    </div>
  );

  if (step === 'validating') {
    return terminal('💵', 'Checking scan link...', 'One moment');
  }
  if (step === 'invalid-token') {
    return terminal('❌', 'Invalid scan link', 'Ask for a new QR code from the expense report page.');
  }
  if (step === 'expired') {
    return terminal('⏱️', 'This QR code has expired', 'Generate a new one on the expense report page and scan again.');
  }
  if (step === 'no-camera') {
    return terminal('📷', 'Camera unavailable', 'Allow camera access in your browser settings, then try again.', (
      <button style={styles.button} onClick={startCamera}>Try Again</button>
    ));
  }
  if (step === 'done') {
    return terminal('✅', 'Receipt sent', 'You can close this page.');
  }
  if (step === 'upload-failed') {
    return terminal('⚠️', "Upload didn't go through",
      uploadError || 'Check your connection and try again.', (
        <div style={styles.bar}>
          {!uploadError && <button style={styles.button} onClick={upload}>Retry</button>}
          <button style={styles.secondaryButton} onClick={startCamera}>Retake</button>
        </div>
      ));
  }

  if (step === 'ready') {
    return (
      <div style={styles.page}>
        <div style={styles.bigIcon}>🧾</div>
        <div style={styles.title}>Add a receipt</div>
        <div style={styles.sub}>{reportTitle}</div>
        <button style={styles.button} onClick={startCamera}>Open Camera</button>
      </div>
    );
  }

  if (step === 'captured' || step === 'uploading') {
    return (
      <div style={styles.page}>
        <div style={styles.title}>Use this photo?</div>
        <img src={captured} alt="Captured receipt" style={styles.capturedImg} />
        <div style={styles.bar}>
          <button
            style={styles.secondaryButton}
            disabled={step === 'uploading'}
            onClick={startCamera}
          >
            Retake
          </button>
          <button
            style={styles.button}
            disabled={step === 'uploading'}
            onClick={upload}
          >
            {step === 'uploading' ? 'Sending...' : 'Send Receipt'}
          </button>
        </div>
      </div>
    );
  }

  // camera
  return (
    <div style={styles.page}>
      <div style={styles.title}>Frame the receipt</div>
      <div style={styles.videoWrap}>
        <video ref={videoRef} autoPlay playsInline muted style={styles.video} />
        <div style={styles.bracket('top left')} />
        <div style={styles.bracket('top right')} />
        <div style={styles.bracket('bottom left')} />
        <div style={styles.bracket('bottom right')} />
      </div>
      <div style={styles.bar}>
        <button style={styles.button} onClick={capture}>Capture</button>
      </div>
    </div>
  );
};

export default ScanReceipt;
